import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  FaGift,
  FaUsers,
  FaBuilding,
  FaCalendarCheck,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  MdEventAvailable,
  MdSell,
  MdRedeem,
  MdOutlineAssignment,
  MdTrendingUp,
} from "react-icons/md";
import {
  getDashboardData,
  selectGiftCertificate,
  GiftCertificateState,
} from "../slices/gift-certificate.slice";

export function GiftCertificateDashboard() {
  const dispatch = useAppDispatch();
  const giftCertificateState = useAppSelector(selectGiftCertificate);

  useEffect(() => {
    dispatch(getDashboardData());
  }, [dispatch]);

  if (giftCertificateState.status === GiftCertificateState.inProgress) {
    return (
      <Box className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress />
      </Box>
    );
  }

  if (giftCertificateState.status === GiftCertificateState.fail) {
    return (
      <Box className="min-h-screen bg-gray-50 p-6">
        <Alert severity="error">{giftCertificateState.message}</Alert>
      </Box>
    );
  }

  const stats = giftCertificateState.dashboardStats;
  const lastCreated = giftCertificateState.lastCreated;

  const statusIcons = {
    Released: <MdEventAvailable className="text-blue-600" size={24} />,
    Received: <MdOutlineAssignment className="text-yellow-600" size={24} />,
    Sold: <MdSell className="text-green-600" size={24} />,
    Redeemed: <MdRedeem className="text-red-600" size={24} />,
  };

  const statusColors = {
    Released: "bg-blue-50 border-blue-200",
    Received: "bg-yellow-50 border-yellow-200",
    Sold: "bg-green-50 border-green-200",
    Redeemed: "bg-red-50 border-red-200",
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
            <FaGift className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Gift Certificate Dashboard
            </h1>
            <p className="text-gray-600">
              Monitor and manage your gift certificates
            </p>
          </div>
        </div>
      </div>

      <Grid container spacing={3}>
        {/* Quick Stats */}
        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {(stats?.by_type?.customer || 0) +
                      (stats?.by_type?.sponsorship || 0)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total GCs
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaGift className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {stats?.by_type?.customer || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Customer GCs
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FaUsers className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {stats?.by_type?.sponsorship || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Sponsorship GCs
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FaBuilding className="text-purple-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {stats?.recent_week || 0}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Created This Week
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <MdTrendingUp className="text-orange-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Breakdown */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Gift Certificates by Status
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(stats?.by_status || {}).map(
                  ([status, count]) => (
                    <Grid item xs={6} md={3} key={status}>
                      <div
                        className={`p-4 rounded-lg border-2 ${
                          statusColors[status as keyof typeof statusColors]
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          {statusIcons[status as keyof typeof statusIcons]}
                          <Typography variant="h5" className="font-bold">
                            {count}
                          </Typography>
                        </div>
                        <Typography variant="body2" className="capitalize">
                          {status}
                        </Typography>
                      </div>
                    </Grid>
                  )
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Last Created */}
        <Grid item xs={12} md={4}>
          <Card className="h-full">
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Recently Created
              </Typography>
              <div className="space-y-4">
                {lastCreated?.customer && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center mb-1">
                      <Chip label="Customer" size="small" color="success" />
                      <Typography variant="body2" color="textSecondary">
                        #{lastCreated.customer.serial_number}
                      </Typography>
                    </div>
                    <Typography variant="body2">
                      {lastCreated.customer.store_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(
                        lastCreated.customer.created_at
                      ).toLocaleDateString()}
                    </Typography>
                  </div>
                )}

                {lastCreated?.sponsorship && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex justify-between items-center mb-1">
                      <Chip
                        label="Sponsorship"
                        size="small"
                        color="secondary"
                      />
                      <Typography variant="body2" color="textSecondary">
                        #{lastCreated.sponsorship.serial_number}
                      </Typography>
                    </div>
                    <Typography variant="body2">
                      {lastCreated.sponsorship.company_name}
                    </Typography>
                    <div className="flex justify-between items-center mt-1">
                      <Typography variant="caption" color="textSecondary">
                        {new Date(
                          lastCreated.sponsorship.created_at
                        ).toLocaleDateString()}
                      </Typography>
                      {lastCreated.sponsorship.expiry_date && (
                        <div className="flex items-center space-x-1">
                          <FaCalendarCheck size={12} className="text-red-500" />
                          <Typography variant="caption" color="error">
                            Expires:{" "}
                            {new Date(
                              lastCreated.sponsorship.expiry_date
                            ).toLocaleDateString()}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!lastCreated?.customer && !lastCreated?.sponsorship && (
                  <div className="text-center py-8 text-gray-500">
                    <FaExclamationTriangle
                      size={32}
                      className="mx-auto mb-2 opacity-50"
                    />
                    <Typography variant="body2">
                      No gift certificates created yet
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
